// src/components/Profile/GroupProviderSwipe.jsx
import React, { useContext, useEffect, useRef, useState, useCallback } from 'react';
import { Container, Spinner, Alert, Button, Row, Col, Card, Badge } from 'react-bootstrap';
import { useSwipeable } from 'react-swipeable';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { TooltipContext } from './TooltipContext';
import CRNAProfileCard from './CRNAProfileCard';
import matchingService from '../../services/matchingService';
import facilityService from '../../services/facilityService';
import listingService from '../../services/listingService';
import api from '../../api';
import './GroupProviderSwipe.css';

// Helper function to format pay periods into readable text
const formatPayPeriod = (period) => {
  if (!period) return '';
  
  const periodMap = {
    'hourly': 'hour',
    'hour': 'hour',
    'annual': 'annually',
    'annually': 'annually',
    'yearly': 'annually',
    'year': 'annually',
    'monthly': 'month',
    'month': 'month',
    'weekly': 'week',
    'week': 'week',
    'daily': 'day',
    'day': 'day'
  };
  
  return periodMap[period.toLowerCase()] || period.toLowerCase();
};

const GroupProviderSwipe = ({ pathListingId }) => {
  const { auth } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [tooltipTarget, setTooltipTarget] = useState(null);
  const [groupId, setGroupId] = useState(null);
  
  // Core data states
  const [facilities, setFacilities] = useState([]);
  const [listingsByFacilityId, setListingsByFacilityId] = useState({});
  const [activeListingId, setActiveListingId] = useState(null);
  const [activeListing, setActiveListing] = useState(null);
  
  // Provider swiping states
  const [providers, setProviders] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorLoading, setErrorLoading] = useState(false);

  // Refs for caching and swipe handling
  const cacheRef = useRef({});
  const cardRef = useRef(null);
  
  // ====== Listing Selection Logic ======
  
  // Initialize active listing from URL parameters or localStorage
  useEffect(() => {
    console.log('Initializing active listing...');
    
    // Priority: pathListingId > query param > localStorage
    const urlParams = new URLSearchParams(location.search);
    const queryListingId = urlParams.get('listingId');
    const storedListingId = localStorage.getItem('activeListingId');
    
    let listingToUse = null;
    
    if (pathListingId) {
      console.log('Using listing ID from path:', pathListingId);
      listingToUse = pathListingId;
    } else if (queryListingId) {
      console.log('Using listing ID from query params:', queryListingId);
      listingToUse = queryListingId;
    } else if (storedListingId) {
      console.log('Using listing ID from localStorage:', storedListingId);
      listingToUse = storedListingId;
    }
    
    if (listingToUse) {
      setActiveListingId(listingToUse);
      localStorage.setItem('activeListingId', listingToUse);
    }
  }, [pathListingId, location.search]);
  
  // Fetch user profile and get group ID
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!auth?.token) {
        console.error('No auth token available');
        setErrorLoading(true);
        setLoading(false);
        return;
      }
      
      try {
        console.log('Fetching user profile to get group ID...');
        const response = await api.get('/auth/me');
        const userData = response.data;
        
        console.log('User profile data:', userData);
        
        if (userData.group && userData.group._id) {
          const userGroupId = userData.group._id;
          console.log('Found group ID:', userGroupId);
          setGroupId(userGroupId);
        } else {
          console.error('No group found in user profile');
          setErrorLoading(true);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        setErrorLoading(true);
        setLoading(false);
      }
    };
    
    fetchUserProfile();
  }, [auth?.token]);

  // Fetch facilities and their listings
  useEffect(() => {
    const fetchFacilitiesAndListings = async () => {
      if (!groupId) {
        console.log('Waiting for group ID...');
        return;
      }
      
      setLoading(true);
      setErrorLoading(false);
      
      // Fetch facilities
      try {
        console.log(`Fetching facilities for group ${groupId}...`);
        const facs = await facilityService.getFacilities(groupId);
        console.log('Facilities fetched:', facs);
        
        if (!facs || facs.length === 0) {
          console.log('No facilities returned from API');
          setFacilities([]);
          setListingsByFacilityId({});
          setLoading(false);
          return;
        }
        
        setFacilities(facs);
        
        // Fetch listings for each facility
        try {
          const map = {};
          for (const facility of facs) {
            const id = facility._id || facility.id;
            try {
              console.log(`Fetching listings for facility ${id}...`);
              const items = await listingService.getListingsForFacility(id, auth.token);
              console.log(`Listings for facility ${id}:`, items);
              map[id] = items || [];
            } catch (listingError) {
              console.error(`Error fetching listings for facility ${id}:`, listingError);
              map[id] = [];
            }
          }
          
          setListingsByFacilityId(map);
        } catch (listingsError) {
          console.error('Error in listings fetch process:', listingsError);
          setErrorLoading(true);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching facilities:', err);
        setErrorLoading(true);
        setLoading(false);
      }
    };
    
    fetchFacilitiesAndListings();
  }, [groupId, auth?.token]);

  // Update active listing object when ID changes
  useEffect(() => {
    if (!activeListingId || !listingsByFacilityId || Object.keys(listingsByFacilityId).length === 0) {
      setActiveListing(null);
      return;
    }
    
    console.log('Finding active listing object for ID:', activeListingId);
    
    // Find the listing object across all facilities
    let foundListing = null;
    
    for (const facilityId in listingsByFacilityId) {
      const listings = listingsByFacilityId[facilityId] || [];
      const match = listings.find(listing => {
        const listingId = listing._id || listing.id;
        return listingId === activeListingId;
      });
      
      if (match) {
        foundListing = match;
        break;
      }
    }
    
    console.log('Active listing found:', foundListing);
    setActiveListing(foundListing);
    
    // Reset providers when listing changes
    setProviders([]);
    cacheRef.current = {};
    setPage(1);
    setHasMore(true);
    setCurrentIndex(0);
  }, [activeListingId, listingsByFacilityId]);
  
  // ====== Provider Fetching Logic ======
  
  // Fetch providers when active listing changes or on page change
  useEffect(() => {
    const fetchProviders = async () => {
      if (!activeListing || !activeListingId) {
        console.log('No active listing, skipping provider fetch');
        return;
      }
      
      if (!hasMore || loadingMore) {
        console.log('No more providers or already loading, skipping fetch');
        return;
      }
      
      setLoadingMore(true);
      console.log(`Fetching providers for listing ${activeListingId}, page ${page}`);
      
      try {
        // Check cache first
        const cacheKey = `${activeListingId}_${page}`;
        if (cacheRef.current[cacheKey]) {
          console.log('Using cached providers for', cacheKey);
          const cachedData = cacheRef.current[cacheKey];
          setProviders(prevProviders => [...prevProviders, ...cachedData.providers]);
          setHasMore(cachedData.hasMore);
          setLoadingMore(false);
          return;
        }
        
        // Fetch from API using the correct method - browseProviders instead of getCandidatesForListing
        const result = await matchingService.browseProviders(page, 10, true, activeListingId);
        console.log('Providers fetched:', result);
        
        const newProviders = result.providers || [];
        const hasMoreProviders = newProviders.length > 0 && result.hasMore !== false;
        
        // Cache the results
        cacheRef.current[cacheKey] = {
          providers: newProviders,
          hasMore: hasMoreProviders
        };
        
        setProviders(prevProviders => [...prevProviders, ...newProviders]);
        setHasMore(hasMoreProviders);
      } catch (error) {
        console.error('Error fetching providers:', error);
      } finally {
        setLoadingMore(false);
      }
    };
    
    fetchProviders();
  }, [activeListing, activeListingId, page, hasMore, loadingMore]);
  
  // ====== Swipe Handling Logic ======
  
  // Handler for swipe actions
  const handleSwipe = useCallback(async (direction) => {
    if (providers.length === 0 || currentIndex >= providers.length) return;
    
    const currentProvider = providers[currentIndex];
    const providerId = currentProvider._id || currentProvider.id;
    
    try {
      if (direction === 'right') {
        console.log(`Swiping right (like) for provider ${providerId}`);
        await matchingService.swipeProvider(activeListingId, providerId, 'right');
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 2000);
      } else {
        console.log(`Swiping left (pass) for provider ${providerId}`);
        await matchingService.swipeProvider(activeListingId, providerId, 'left');
      }
      
      setDirection(direction);
      
      // Move to next provider after a short delay
      setTimeout(() => {
        setDirection('');
        setCurrentIndex(prevIndex => {
          const nextIndex = prevIndex + 1;
          
          // Load more if getting close to the end
          if (nextIndex >= providers.length - 3 && hasMore && !loadingMore) {
            console.log('Getting close to end, loading more providers');
            setPage(prevPage => prevPage + 1);
          }
          
          return nextIndex;
        });
      }, 300);
    } catch (error) {
      console.error('Error recording swipe:', error);
    }
  }, [providers, currentIndex, activeListingId, hasMore, loadingMore]);
  
  // Handle swipe gestures
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => handleSwipe('left'),
    onSwipedRight: () => handleSwipe('right'),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true
  });
  
  // ====== Action Handlers ======
  
  // Handle selecting a listing
  const handleSelectListing = useCallback((listingId) => {
    console.log('Selecting listing:', listingId);
    localStorage.setItem('activeListingId', listingId);
    
    // Navigate to the path-based URL
    navigate(`/group-provider-swipe/${listingId}`, { replace: true });
  }, [navigate]);
  
  // Clear active listing
  const handleClearListing = useCallback(() => {
    console.log('Clearing active listing');
    localStorage.removeItem('activeListingId');
    navigate('/group-provider-swipe', { replace: true });
  }, [navigate]);
  
  // ====== Render Helper Functions ======
  
  // Render the listing selector
  const renderListingSelector = () => {
    // Compute all listings across facilities
    const allListings = [];
    
    for (const facilityId in listingsByFacilityId) {
      const facilityListings = listingsByFacilityId[facilityId] || [];
      const facility = facilities.find(f => (f._id || f.id) === facilityId);
      
      facilityListings.forEach(listing => {
        allListings.push({
          ...listing,
          facilityName: facility?.name || 'Unknown Facility'
        });
      });
    }
    
    if (allListings.length === 0) {
      return (
        <div className="glass-container p-5 text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-4"
          >
            <span style={{ fontSize: '3.5rem' }}>📋</span>
          </motion.div>
          <motion.h4 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="mb-3 fw-bold"
          >
            No Listings Found
          </motion.h4>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="text-muted mb-4"
          >
            You need to create job listings before you can browse providers.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <Button 
              variant="primary" 
              onClick={() => navigate('/group-intake')}
              className="d-flex mx-auto align-items-center justify-content-center gap-2 py-2 px-4"
              style={{ borderRadius: '30px', fontWeight: '600' }}
            >
              <i className="bi bi-plus-circle"></i> Create a Listing
            </Button>
          </motion.div>
        </div>
      );
    }
    
    return (
      <div className="glass-container p-4 mb-4">
        <motion.h4 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-4 text-center fw-bold"
          style={{ 
            backgroundImage: 'linear-gradient(90deg, #0d6efd, #0dcaf0)', 
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontSize: '1.8rem'
          }}
        >
          Select a Listing to Browse Providers
        </motion.h4>
        
        <Row xs={1} md={2} lg={3} className="g-4">
          {allListings.map((listing, index) => {
            const listingId = listing._id || listing.id;
            
            // Debug compensation data
            console.log(`Listing ${listingId} compensation:`, listing.compensation);
            console.log(`Listing ${listingId} compensation type:`, typeof listing.compensation);
            
            // Extract compensation value with correct structure
            let compensationValue = 0;
            let payPeriod = '';
            
            if (listing.compensation) {
              // New API structure with compensation.amount
              if (typeof listing.compensation === 'object' && listing.compensation.amount) {
                compensationValue = listing.compensation.amount;
                payPeriod = listing.compensation.period || '';
              } 
              // Legacy structure or different format
              else if (typeof listing.compensation === 'object') {
                compensationValue = listing.compensation.max || listing.compensation.value || 0;
                payPeriod = listing.payPeriod || '';
              } else if (typeof listing.compensation === 'string') {
                compensationValue = parseInt(listing.compensation) || 0;
                payPeriod = listing.payPeriod || '';
              } else if (typeof listing.compensation === 'number') {
                compensationValue = listing.compensation || 0;
                payPeriod = listing.payPeriod || '';
              }
            }
            
            return (
              <Col key={listingId}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.4 }}
                >
                  <Card 
                    className="h-100 listing-card premium-card" 
                    onClick={() => handleSelectListing(listingId)}
                  >
                    <div className="position-relative">
                      <div 
                        className="position-absolute w-100" 
                        style={{ 
                          height: '8px', 
                          top: 0, 
                          background: 'linear-gradient(90deg, #0d6efd, #0dcaf0)',
                          borderTopLeftRadius: '12px',
                          borderTopRightRadius: '12px'
                        }}
                      />
                      <Card.Body className="pt-4">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <h5 className="listing-title">{listing.title || listing.role || 'CRNA Position'}</h5>
                          <Badge 
                            bg="primary" 
                            pill 
                            style={{ 
                              background: 'linear-gradient(90deg, #0d6efd, #0dcaf0)',
                              fontSize: '0.7rem',
                              padding: '0.5em 0.8em'
                            }}
                          >
                            {listing.type || 'PERMANENT'}
                          </Badge>
                        </div>
                        
                        <div className="facility-name mb-3">
                          <i className="bi bi-building me-2 text-primary"></i>
                          {listing.facilityName}
                        </div>
                        
                        <div className="info-rows">
                          {listing.city && listing.state && (
                            <div className="info-row mb-2">
                              <i className="bi bi-geo-alt-fill text-primary"></i>
                              <span className="ms-2">{listing.city}, {listing.state}</span>
                            </div>
                          )}
                          
                          {listing.compensation && (
                            <div className="info-row mb-2">
                              <i className="bi bi-cash-stack text-primary"></i>
                              <span className="ms-2 fw-semibold">
                                ${compensationValue.toLocaleString()}
                                {payPeriod && ` / ${formatPayPeriod(payPeriod)}`}
                              </span>
                            </div>
                          )}
                          
                          {listing.scheduleTypes && listing.scheduleTypes.length > 0 && (
                            <div className="info-row mb-2">
                              <i className="bi bi-clock text-primary"></i>
                              <span className="ms-2">
                                {listing.scheduleTypes.join(', ')}
                              </span>
                            </div>
                          )}
                          
                          {listing.assignmentTypes && listing.assignmentTypes.length > 0 && (
                            <div className="info-row mb-2">
                              <i className="bi bi-briefcase text-primary"></i>
                              <span className="ms-2">
                                {listing.assignmentTypes.join(', ')}
                              </span>
                            </div>
                          )}
                          
                          {listing.startDate && (
                            <div className="info-row mb-2">
                              <i className="bi bi-calendar-check text-primary"></i>
                              <span className="ms-2">
                                Starts: {new Date(listing.startDate).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        <div className="mt-4 d-flex justify-content-center">
                          <Button 
                            variant="primary" 
                            className="browse-providers-btn w-100"
                            style={{
                              background: 'linear-gradient(90deg, #0d6efd, #0dcaf0)',
                              border: 'none',
                              boxShadow: '0 4px 12px rgba(13, 110, 253, 0.2)',
                              borderRadius: '6px',
                              fontWeight: '600',
                              padding: '0.5rem 1rem',
                              transition: 'all 0.3s ease'
                            }}
                          >
                            <i className="bi bi-search me-2"></i>
                            Browse Providers
                          </Button>
                        </div>
                      </Card.Body>
                    </div>
                  </Card>
                </motion.div>
              </Col>
            );
          })}
        </Row>
      </div>
    );
  };
  
  // Render the provider card for swiping
  const renderProviderCard = () => {
    // Initial loading state
    if (providers.length === 0 && loadingMore) {
      return (
        <div className="text-center p-5">
          <h5>Loading providers...</h5>
          <Spinner animation="border" size="sm" className="mt-2" />
        </div>
      );
    }
    
    // Empty providers array after loading
    if (providers.length === 0 && !loadingMore) {
      return (
        <div className="glass-container p-5 text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-4"
          >
            <span style={{ fontSize: '3.5rem' }}>🔍</span>
          </motion.div>
          <motion.h4 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="mb-3 fw-bold"
          >
            No Providers Found
          </motion.h4>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="text-muted mb-4"
          >
            There are currently no available providers matching this listing.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
          >
            <Button 
              variant="outline-primary" 
              onClick={handleClearListing} 
              className="mt-2"
            >
              <i className="bi bi-arrow-left me-2"></i>
              Select Another Listing
            </Button>
          </motion.div>
        </div>
      );
    }
    
    // No more providers to show
    if (currentIndex >= providers.length) {
      return (
        <div className="glass-container p-5 text-center">
          <motion.h5 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-3"
          >
            No more providers to show
          </motion.h5>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-muted small mt-3"
          >
            We'll notify you when new matches become available
          </motion.p>
          <Button 
            variant="outline-primary" 
            onClick={handleClearListing} 
            className="mt-2"
          >
            Browse Another Listing
          </Button>
        </div>
      );
    }
    
    const currentProvider = providers[currentIndex];
    
    return (
      <div 
        {...swipeHandlers} 
        ref={cardRef} 
        className="position-relative"
      >
        <motion.div
          animate={direction ? { x: direction === 'right' ? 1000 : -1000 } : { x: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          className="w-100"
        >
          <CRNAProfileCard provider={currentProvider} />
        </motion.div>
        
        {showConfetti && (
          <div className="confetti-container">
            {[...Array(50)].map((_, i) => (
              <div 
                key={i} 
                className="confetti"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDuration: `${Math.random() * 3 + 2}s`,
                  backgroundColor: ['#ff6b6b', '#48dbfb', '#1dd1a1', '#feca57'][Math.floor(Math.random() * 4)]
                }}
              />
            ))}
          </div>
        )}
      </div>
    );
  };
  
  // ====== Main Render Logic ======
  
  // Show loading state
  if (loading) {
    return (
      <Container className="py-5 text-center">
        <div className="glass-container p-5">
          <h4 className="mb-4">Loading your listings...</h4>
          <Spinner animation="border" />
          <p className="mt-3 text-muted">This may take a moment</p>
        </div>
      </Container>
    );
  }
  
  // Show error state
  if (errorLoading) {
    return (
      <Container className="py-5 text-center">
        <div className="glass-container p-5">
          <h4 className="mb-3">Unable to load listings</h4>
          <p className="text-muted mb-4">There was a problem connecting to the server</p>
          <Button 
            variant="primary" 
            onClick={() => window.location.reload()}
            className="me-2"
          >
            Try Again
          </Button>
          <Button 
            variant="outline-primary" 
            onClick={() => navigate('/group-dashboard')}
          >
            Go to Dashboard
          </Button>
        </div>
      </Container>
    );
  }
  
  return (
    <TooltipContext.Provider value={{ tooltipTarget, setTooltipTarget }}>
      <Container className="py-4">
        {!activeListing ? (
          // No active listing selected, show listing selector
          renderListingSelector()
        ) : (
          // Active listing selected, show provider swipe interface
          <div className="glass-container p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h4 className="mb-0">
                {activeListing.role || 'CRNA Position'} 
                <Badge bg="primary" className="ms-2" pill>
                  {activeListing.type || 'Permanent'}
                </Badge>
              </h4>
              <Button 
                variant="outline-secondary" 
                size="sm" 
                onClick={handleClearListing}
              >
                Change Listing
              </Button>
            </div>
            
            {renderProviderCard()}
            
            {currentIndex < providers.length && (
              <div className="d-flex justify-content-center mt-4">
                <div className="swipe-controls">
                  <div className="control-button" onClick={() => handleSwipe('left')}>
                    <Button 
                      variant="outline-danger" 
                      className="rounded-circle"
                    >
                      <i className="bi bi-x-lg"></i>
                    </Button>
                    <span className="control-label">Pass</span>
                  </div>
                  <div className="control-button" onClick={() => handleSwipe('right')}>
                    <Button 
                      variant="primary" 
                      className="rounded-circle"
                    >
                      <i className="bi bi-check-lg"></i>
                    </Button>
                    <span className="control-label">Connect</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </Container>
    </TooltipContext.Provider>
  );
};

export default GroupProviderSwipe;
